import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SuperAdminSidebar from "../SuperAdminSidebar";
import ManagerSidebar from "../ManagerSidebar";
import LeaderSidebar from "../LeaderSidebar";
import MemberSidebar from "../MemberSidebar";
import AdminNavbar from './AdminNavbar';
import './Announcements.css';

const BASE = 'https://skcapi.btcjapan.co';
const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
};

const fileToBase64Payload = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
        name: file.name,
        type: file.type,
        size: file.size,
        data: reader.result.split(',')[1],
    });
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

const NewAnnouncements = () => {
    const userType = localStorage.getItem('usertype');
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const editId = params.get('id');

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        share_clients: false,
        status: 'Unpublished',
    });
    const [attachment, setAttachment] = useState(null);
    const [existingAttachment, setExistingAttachment] = useState(null);
    const [loading, setLoading] = useState(!!editId);
    const [saving, setSaving] = useState(false);

    const fileInputRef = useRef(null);
    // const descRef = useRef(null);

    // Load existing when editing
    useEffect(() => {
        if (!editId) return;
        (async () => {
            try {
                const res = await axios.get(`${BASE}/get_single_announcement.php?id=${editId}`, {
                    headers: authHeader()
                });
                if (res.data.success) {
                    const d = res.data.data;
                    setFormData({
                        title: d.title || '',
                        description: d.description || '',
                        start_date: d.start_date || '',
                        end_date: d.end_date || '',
                        share_clients: Number(d.share_clients) === 1,
                        status: d.status || 'Unpublished',
                    });
                    setExistingAttachment(d.attachment || null);
                }
            } catch (err) {
                console.error(err);
                alert('Failed to load announcement');
            } finally {
                setLoading(false);
            }
        })();
    }, [editId]);


    /*
    useEffect(() => {
        if (!descRef.current) return;
        const currentHtml = descRef.current.innerHTML;
        const targetHtml = formData.description || '';
        if (currentHtml !== targetHtml) {
            descRef.current.innerHTML = targetHtml;
        }
    }, [loading]);
    */

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    // Description toolbar — minimal Rich Text via document.execCommand
    /*
    const execCmd = (cmd, val) => {
        
        descRef.current?.focus();
        
        try {
            const sel = window.getSelection();
            if (sel && descRef.current) {
                const range = document.createRange();
                range.selectNodeContents(descRef.current);
                range.collapse(false);
                sel.removeAllRanges();
                sel.addRange(range);
            }
        } catch (_) {  }
        document.execCommand(cmd, false, val);
    };
    */

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title.trim()) { alert('Title is required'); return; }
        if (!formData.start_date)   { alert('Start Date is required'); return; }
        if (!formData.end_date)     { alert('End Date is required'); return; }

        setSaving(true);
        try {
            let attachmentPayload = null;
            if (attachment) {
                attachmentPayload = await fileToBase64Payload(attachment);
            }

            const payload = {
                mode: editId ? 'edit' : 'create',
                id: editId ? Number(editId) : undefined,
                title: formData.title,
                description: formData.description || '',
                start_date: formData.start_date,
                end_date: formData.end_date,
                share_clients: formData.share_clients,
                status: formData.status,
                attachment: attachmentPayload,
            };

            const res = await axios.post(`${BASE}/create_update_announcement.php`, payload, {
                headers: { ...authHeader(), 'Content-Type': 'application/json' }
            });

            if (res.data.success) {
                alert(editId ? 'Announcement updated!' : 'Announcement created!');
                navigate('/announcements');
            } else {
                alert(res.data.error || 'Failed to save');
            }
        } catch (err) {
            console.error(err);
            alert('Error saving announcement: ' + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleFileChange = (e) => {
        const f = e.target.files?.[0];
        if (f) setAttachment(f);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) setAttachment(f);
    };

    if (loading) {
        return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading…</div>;
    }

    return (
        <div className="admin-container">
            <div className="sidebar-container">
                {userType === "administrator" && <SuperAdminSidebar />}
                {userType === "leader" && <LeaderSidebar />}
                {userType === "manager" && <ManagerSidebar />}
                {userType === "member" && <MemberSidebar />}
            </div>

            <div className="admin-content">
                <AdminNavbar />

                <div className="main-content">
                    <div className="ann-page">
                        <header className="ann-hero">
                            <div>
                                <h1>{editId ? 'Edit Announcement' : 'New Announcements'}</h1>
                                <p>{editId ? 'Update this announcement' : 'Create a new announcement for your users'}</p>
                            </div>
                            <button className="ann-back-btn" onClick={() => navigate('/admin/announcements')}>
                                ← Back to Announcements
                            </button>
                        </header>

                        <form className="ann-form" onSubmit={handleSubmit}>
                            <div className="ann-form-row">
                                <label>Title <span className="req">*</span></label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="ann-form-row">
                                <label>Description</label>
                                <textarea
                                    name="description"
                                    className="ann-textarea"
                                    value={formData.description}
                                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    rows={6}
                                    placeholder="Write your announcement here…"
                                />
                            </div>

                            <div className="ann-form-row">
                                <label>Start Date <span className="req">*</span></label>
                                <input
                                    type="date"
                                    name="start_date"
                                    value={formData.start_date}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="ann-form-row">
                                <label>End Date <span className="req">*</span></label>
                                <input
                                    type="date"
                                    name="end_date"
                                    value={formData.end_date}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="ann-form-row">
                                <label>Attachment</label>
                                <div
                                    className="ann-drop"
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <div className="ann-drop-title">
                                        <strong>Drag files</strong> to upload
                                    </div>
                                    <div className="ann-drop-sub">(or click)</div>
                                    {attachment && (
                                        <div className="ann-drop-selected">
                                            📎 {attachment.name}
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); setAttachment(null); }}
                                            >×</button>
                                        </div>
                                    )}
                                    {!attachment && existingAttachment && (
                                        <div className="ann-drop-selected">
                                            Current: {existingAttachment}
                                        </div>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        style={{ display: 'none' }}
                                        onChange={handleFileChange}
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*"
                                    />
                                </div>
                            </div>

                            <div className="ann-form-row ann-form-row--inline">
                                <label>Share With</label>
                                <label className="ann-check">
                                    <input
                                        type="checkbox"
                                        name="share_clients"
                                        checked={formData.share_clients}
                                        onChange={handleChange}
                                    />
                                    All Clients
                                </label>
                            </div>

                            <div className="ann-form-row ann-form-row--inline">
                                <label>Status</label>
                                <div className="ann-status-row">
                                    <label className="ann-check">
                                        <input
                                            type="radio"
                                            name="status"
                                            value="Published"
                                            checked={formData.status === 'Published'}
                                            onChange={handleChange}
                                        />
                                        Published
                                    </label>
                                    <label className="ann-check">
                                        <input
                                            type="radio"
                                            name="status"
                                            value="Unpublished"
                                            checked={formData.status === 'Unpublished'}
                                            onChange={handleChange}
                                        />
                                        Un Published
                                    </label>
                                </div>
                            </div>

                            <div className="ann-form-actions">
                                <button
                                    type="button"
                                    className="ann-btn-secondary"
                                    onClick={() => navigate('/admin/announcements')}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="ann-btn-primary"
                                    disabled={saving}
                                >
                                    {saving ? 'Saving…' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewAnnouncements;